import React from "react";
import { Divider, Grid, Text, useMantineColorScheme } from "@mantine/core";
import {
  formatCompactNumber,
  formatProjectNumber,
  useCurrency,
} from "../../../../utils/helpers";
import PropTypes from "prop-types";
import { ENERGY_BALANCE_LABELS, formatName } from "../ENERGY_BALANCE_CONFIG";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { projectDetailsAtom } from "../../../../utils/atoms/projectDetailsAtom";

const EnergyTable = ({ tableData = {}, gridType }) => {
  const { colorScheme } = useMantineColorScheme();
  const { t } = useTranslation();
  const [projectDetails] = useAtom(projectDetailsAtom);
  const numberFormat = projectDetails?.projectDTO?.numberFormat;
  const currency = useCurrency();
  const textColor = colorScheme === "dark" ? "#d5d7e0" : "#495057";
  const OFF_GRID_HIDDEN_LABELS = [
    "savings",
    "energy",
    "demandCharge",
    "unmetEnergyConnected",
    "excessEnergyConnected",
  ];
  const visibleLabels =
    gridType === "OFF_GRID"
      ? ENERGY_BALANCE_LABELS?.filter(
          ({ name }) => !OFF_GRID_HIDDEN_LABELS.includes(name)
        )
      : ENERGY_BALANCE_LABELS;
  const getPlainText = (node) => {
    if (node == null) return "";
    if (typeof node === "string" || typeof node === "number") return String(node);
    if (Array.isArray(node)) return node.map(getPlainText).join("");
    if (React.isValidElement(node)) return getPlainText(node.props.children);
    return "";
  };

  const getLabelIndentation = (label) => label === "energy" || label === "demandCharge";

  return (
    <>
      {gridType !== "OFF_GRID" && (
        <>
          <Grid>
            <Grid.Col span={8}>
              <Text fw={700} size="md" color={textColor}>
                {t("result.energyBalance.table.preMicrogridCost")}
              </Text>
            </Grid.Col>
            <Grid.Col span={4} fw={700} size="md">
              <Text
                align="right"
                title={`${currency} ${formatProjectNumber(
                  tableData?.preMicrogridCost ?? 0
                , { numberFormat })}`}
              >
                {currency} {formatCompactNumber(tableData?.preMicrogridCost ?? 0, numberFormat)}
              </Text>
            </Grid.Col>
          </Grid>
          <Divider my="sm" color="blue" size="md" />
          <Grid>
            <Grid.Col span={8}>
              <Text fw={700} size="md" color={textColor}>
                {t("result.energyBalance.table.postMicrogridCost")}
              </Text>
            </Grid.Col>
            <Grid.Col span={4} fw={700} size="md">
              <Text
                align="right"
                title={`${currency} ${formatProjectNumber(
                  tableData?.postMicrogridCost ?? 0
                , { numberFormat })}`}
              >
                {currency} {formatCompactNumber(tableData?.postMicrogridCost ?? 0, numberFormat)}
              </Text>
            </Grid.Col>
          </Grid>
          <Divider my="sm" color="green" size="md" />
        </>
      )}
      {visibleLabels?.map(({ name, symbol }) => {
        const value = tableData?.[name] ?? 0;
        const symText = typeof symbol === "string" ? symbol : getPlainText(symbol);
        const isCurrency = symbol === "$";
        const title = isCurrency
          ? `${currency} ${formatProjectNumber(value, { numberFormat })}`
          : `${formatProjectNumber(value, { numberFormat })}${symText ? " " + symText : ""}`;

        return (
          <React.Fragment key={name}>
            <Grid>
              <Grid.Col span={6} mr={0}>
                <Text
                  fw={getLabelIndentation(name) ? 400 : 800}
                  pl={getLabelIndentation(name) ? 20 : 0}
                  color={textColor}
                  size="md"
                >
                  {gridType === "OFF_GRID" && name === "unmetEnergyOffgrid"
                    ? "Unmet Energy"
                    : gridType === "OFF_GRID" && name === "excessEnergyOffgrid"
                    ? "Excess Energy"
                    : formatName(name)}
                </Text>
              </Grid.Col>
              <Grid.Col span={6} ml={0}>
                <Text align="right" fw={700} size="md" title={title}>
                  {isCurrency ? (
                    `${currency} ${formatCompactNumber(value, numberFormat)}`
                  ) : (
                    <>
                      {name === "unmetHours"
                        ? formatProjectNumber(value, { numberFormat })
                        : formatCompactNumber(value, numberFormat)}
                      {symbol ? " " : ""}
                      {symbol}
                    </>
                  )}
                </Text>
              </Grid.Col>
            </Grid>
            <Divider my="xs" />
          </React.Fragment>
        );
      })}
    </>
  );
};

EnergyTable.propTypes = {
  tableData: PropTypes.shape({
    preMicrogridCost: PropTypes.number,
    postMicrogridCost: PropTypes.number,
    selfConsumption: PropTypes.number,
    savings: PropTypes.number,
    energy: PropTypes.number,
    demandCharge: PropTypes.number,
    unmetHours: PropTypes.number,
    unmetEnergyConnected: PropTypes.number,
    unmetEnergyOffgrid: PropTypes.number,
    excessEnergyOffgrid: PropTypes.number,
    excessEnergyConnected: PropTypes.number,
  }),
  gridType: PropTypes.string,
};

export default EnergyTable;
